import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from './AuthContext';

interface EntitlementData {
  plan: string;
  status: string;
  features: string[];
  limits: Record<string, number>;
  usage: { users: number; customers: number; invoices: number; employees: number; aiRequests: number };
}

interface EntitlementContextType {
  data: EntitlementData | null;
  loading: boolean;
  canUse: (feature: string) => boolean;
  getLimit: (key: string) => number;
  getUsage: (key: string) => number;
  isAtLimit: (key: string) => boolean;
  refresh: () => Promise<void>;
  upgradePlan: (plan: string) => Promise<boolean>;
}

const defaults: EntitlementData = {
  plan: 'starter', status: 'active', features: [], limits: {}, usage: { users: 0, customers: 0, invoices: 0, employees: 0, aiRequests: 0 },
};

const EntitlementContext = createContext<EntitlementContextType>({
  data: null, loading: true,
  canUse: () => true, getLimit: () => -1, getUsage: () => 0, isAtLimit: () => false,
  refresh: async () => {}, upgradePlan: async () => false,
});

export function EntitlementProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<EntitlementData | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setData(null); setLoading(false); return; }
    try {
      const [entRes, usageRes] = await Promise.all([
        apiFetch('/api/entitlements'),
        apiFetch('/api/entitlements/usage'),
      ]);
      const ent = await entRes.json();
      const usage = await usageRes.json();
      setData({ ...ent, usage });
    } catch {
      setData(defaults);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 60s to pick up admin changes (plan, status, etc.)
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(refresh, 60_000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refresh]);

  const canUse = useCallback((feature: string) => {
    if (!data) return true;
    return data.features.includes(feature);
  }, [data]);

  const getLimit = useCallback((key: string) => {
    return data?.limits[key] ?? -1;
  }, [data]);

  const getUsage = useCallback((key: string) => {
    return (data?.usage as any)?.[key] ?? 0;
  }, [data]);

  const isAtLimit = useCallback((key: string) => {
    const limit = data?.limits[`max_${key}`] ?? -1;
    if (limit === -1) return false;
    const usage = (data?.usage as any)?.[key] ?? 0;
    return usage >= limit;
  }, [data]);

  const upgradePlan = useCallback(async (plan: string) => {
    // If user has active subscription, change it directly
    if (data?.plan && data.plan !== 'starter') {
      try {
        const res = await apiFetch('/api/billing/change', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan }),
        });
        if (res.ok) { await refresh(); return true; }
        // If change failed but plan is starter, still refresh
        if (plan === 'starter') { await refresh(); return true; }
        return false;
      } catch { return false; }
    }
    // No subscription yet — create checkout
    if (plan === 'starter') return true;
    try {
      const res = await apiFetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
      return true;
    } catch { return false; }
  }, [data, refresh]);

  return (
    <EntitlementContext.Provider value={{ data, loading, canUse, getLimit, getUsage, isAtLimit, refresh, upgradePlan }}>
      {children}
    </EntitlementContext.Provider>
  );
}

export const useEntitlement = () => useContext(EntitlementContext);
